import { defineMessages } from "react-intl";

export const MESSAGES = defineMessages({
  pageTitle: {
    id: "funding-application: accounts page title",
    defaultMessage: "Add all your bank accounts",
  },
  pageSubtitle: {
    id: "funding-application: accounts page subtitle",
    defaultMessage: "Include current accounts, credit cards, and any overdraft or credit line.",
  },
  addAccountInstantly: {
    id: "funding-application: add account instantly button",
    defaultMessage: "Add connection instantly",
  },
  addStatementsManually: {
    id: "funding-application: add statements manually button",
    defaultMessage: "Add or drop statements",
  },
  emptyStateConnectionCardTitle: {
    id: "funding-application: accounts page empty state connection card title",
    defaultMessage: "Connect your bank",
  },
  emptyStateConnectionCardSubtitle: {
    id: "funding-application: accounts page empty state connection card subtitle",
    defaultMessage: "Instant verification, usually under an hour.",
  },
  emptyStateRecommendedBadge: {
    id: "funding-application: accounts page empty state recommended badge",
    defaultMessage: "Recommended",
  },
  emptyStateRequiredBadge: {
    id: "funding-application: accounts page empty state required badge",
    defaultMessage: "Required",
  },
  emptyStateUploadCardTitle: {
    id: "funding-application: accounts page empty state upload card title",
    defaultMessage: "Upload statements",
  },
  emptyStateUploadCardSubtitle: {
    id: "funding-application: accounts page empty state upload card subtitle",
    defaultMessage: "Can take days, may require follow-up.",
  },
  uploadAvailableAfterBankConnection: {
    id: "funding-application: accounts page upload available after bank connection",
    defaultMessage: "Available after connecting your bank.",
  },
  ukBankingSatisfiedDisclosure: {
    id: "funding-application: accounts page uk banking satisfied disclosure",
    defaultMessage:
      "By clicking {nextStep} to continue, you confirm your details are correct. We may share your personal information with fraud prevention agencies, and if fraud is detected, you may be refused services, financing, or employment. Read our <privacyLink>Privacy Notice</privacyLink>.",
  },
  timeInBusinessBanner: {
    id: "funding-application: accounts page time in business explainer",
    defaultMessage:
      "Based on your business model, you'll need at least <b>{months} months</b> of trading history to qualify for funding. Connect your bank now and we'll let you know when you're eligible.",
  },
  floatingFilesListTitle: {
    id: "funding-application: accounts page floating files list title",
    defaultMessage: "Processing files",
  },
  floatingFilesListProgress: {
    id: "funding-application: accounts page floating files list progress",
    defaultMessage: "{done}/{total}",
  },
  uploadFailedMessage: {
    id: "funding-application: accounts page upload failed message",
    defaultMessage: "Upload failed. Try again.",
  },
  cancelAction: {
    id: "funding-application: accounts page cancel action",
    defaultMessage: "Cancel",
  },
  retryAction: {
    id: "funding-application: accounts page retry action",
    defaultMessage: "Retry",
  },
  retryAllAction: {
    id: "funding-application: accounts page retry all action",
    defaultMessage: "Retry all",
  },
  doneAction: {
    id: "funding-application: accounts page done action",
    defaultMessage: "Done",
  },
  connectedBadge: {
    id: "funding-application: accounts page connected badge",
    defaultMessage: "Connected",
  },
  readyBadge: {
    id: "funding-application: accounts page ready badge",
    defaultMessage: "Ready",
  },
  manageAccountAction: {
    id: "funding-application: accounts page manage account action",
    defaultMessage: "Manage",
  },
  bankAccountsSectionTitle: {
    id: "funding-application: accounts page bank accounts section title",
    defaultMessage: "Bank accounts",
  },
  connectedWithProvider: {
    id: "funding-application: accounts page connected with provider subtitle",
    defaultMessage: "Connected with {provider} ({currency})",
  },
  connectedWithProviderNoCurrency: {
    id: "funding-application: accounts page connected with provider no currency subtitle",
    defaultMessage: "Connected with {provider}",
  },
  businessAccountFallback: {
    id: "funding-application: accounts page business account fallback label",
    defaultMessage: "Business account",
  },
  requiredFromYouHeading: {
    id: "funding-application: accounts page required from you heading",
    defaultMessage: "Required from you",
  },
  requiredFromYouBodyText: {
    id: "funding-application: accounts page required from you body text",
    defaultMessage:
      "We need continuous, up-to-date banking data from {startDate} onwards for each account linked to your business. If you're uploading statements, they must be PDF or CSV files under 50MB. {viewMore}",
  },
  viewMore: {
    id: "funding-application: accounts page view more link",
    defaultMessage: "View more.",
  },
  requirementsModalTitle: {
    id: "funding-application: accounts page requirements modal title",
    defaultMessage: "Required from you",
  },
  requirementsModalBody: {
    id: "funding-application: accounts page requirements modal body",
    defaultMessage:
      "We need at least {requiredMonths} months of continuous banking data for every account associated with your business.",
  },
  requirementsModalListHeading: {
    id: "funding-application: accounts page requirements modal list heading",
    defaultMessage: "We'll ask for more information if the data has:",
  },
  requirementsModalRuleGaps: {
    id: "funding-application: accounts page requirements modal rule gaps",
    defaultMessage: "gaps longer than 14 days",
  },
  requirementsModalRuleNoTransactions: {
    id: "funding-application: accounts page requirements modal rule no transactions",
    defaultMessage: "no transactions within the last 14 days",
  },
  requirementsModalFootnote: {
    id: "funding-application: accounts page requirements modal footnote",
    defaultMessage:
      "If your business had a genuine quiet period, the account is new, or it was recently closed, you'll get a chance to explain this later.",
  },
  connectModalTitle: {
    id: "funding-application: accounts page connect modal title",
    defaultMessage: "Connect your bank",
  },
  connectModalBody: {
    id: "funding-application: accounts page connect modal body",
    defaultMessage: "Select your bank's country. We'll use this to connect your account securely.",
  },
  connectModalContinue: {
    id: "funding-application: accounts page connect modal continue",
    defaultMessage: "Continue",
  },
  bankLocationLabel: {
    id: "funding-application: accounts page bank location label",
    defaultMessage: "Bank location",
  },
  accountDetailsTypeAndCurrency: {
    id: "funding-application: accounts page account details type and currency",
    defaultMessage: "{accountType} account ({currency})",
  },
  accountDetailsType: {
    id: "funding-application: accounts page account details type",
    defaultMessage: "{accountType} account",
  },
  accountDetailsBodyText: {
    id: "funding-application: accounts page account details body text",
    defaultMessage: "Your account is connected. We'll process this data as part of your application.",
  },
  extractedStatementsSectionTitle: {
    id: "funding-application: accounts page extracted statements section title",
    defaultMessage: "Statements uploaded",
  },
  extractionProcessingText: {
    id: "funding-application: accounts page extraction processing text",
    defaultMessage: "Reading statement details...",
  },
  // temporary copy pending confirmed design for the extraction failure state
  extractionFailedText: {
    id: "funding-application: accounts page extraction failed text",
    defaultMessage: "We could not read this statement. Try uploading it again.",
  },
  extractedAccountWithMask: {
    id: "funding-application: accounts page extracted account with mask",
    defaultMessage: "{accountType} account ··{mask} ({currency})",
  },
});
